// src/components/KidsaTestimonial.tsx - VERSIÓN MEJORADA CON SLIDER CONTROLABLE
import React, { useEffect, useRef } from 'react';
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

interface Testimonial {
  quote: string;
  author: string;
  quoteIcon?: string;
  backgroundStyle?: 'default' | 'bg-2' | 'bg-3';
}

interface KidsaTestimonialProps {
  subtitle?: string;
  title?: string;
  testimonials: Testimonial[];
}

const KidsaTestimonial: React.FC<KidsaTestimonialProps> = ({
  subtitle = "testimonials",
  title = "Parents' words are the key <br> to happy kids",
  testimonials
}) => {
  const swiperRef = useRef<Swiper | null>(null);

  useEffect(() => {
    // Inicializar Swiper con configuración mejorada
    if (swiperRef.current === null) {
      swiperRef.current = new Swiper('.testimonial-slider', {
        modules: [Autoplay, Pagination],
        slidesPerView: 1,
        spaceBetween: 30,
        loop: true,
        speed: 1000, // Transición más suave (1 segundo)
        autoplay: {
          delay: 5000, // 5 segundos por slide (antes era muy rápido)
          disableOnInteraction: false, // Continúa después de interacción
          pauseOnMouseEnter: true // Pausa al hacer hover
        },
        pagination: {
          el: '.swiper-dot .dot',
          clickable: true,
          renderBullet: function (index, className) {
            return '<span class="' + className + '"></span>';
          }
        },
        breakpoints: {
          768: {
            slidesPerView: 2,
            spaceBetween: 30
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 30
          }
        },
        // Permitir control con teclado
        keyboard: {
          enabled: true,
          onlyInViewport: true
        },
        // Permitir arrastrar
        simulateTouch: true,
        grabCursor: true
      });
    }

    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true);
        swiperRef.current = null;
      }
    };
  }, []);

  // Controles manuales
  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handlePause = () => {
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.stop();
    }
  };

  const handlePlay = () => {
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.start();
    }
  };

  return (
    <section className="testimonial-section fix section-padding" id="comunicados">
      <div className="tree-shape float-bob-y">
        <img src="/assets/kidsa/assets/img/crayons.png" alt="shape-img" />
      </div>
      <div className="right-shape">
        <img src="/assets/kidsa/assets/img/testi-r-shape.png" alt="shape-img" />
      </div>
      <div className="bee-shape float-bob-y">
        <img src="/assets/kidsa/assets/img/testi-bee-shape.png" alt="shape-img" />
      </div>
      <div className="container">
        <div className="section-title text-center">
          <span className="wow fadeInUp">{subtitle}</span>
          <h2 
            className="wow fadeInUp text-transform-none" 
            data-wow-delay=".3s"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        </div>

        {/* Controles del slider */}
        <div className="testimonial-controls">
          <button 
            type="button" 
            className="testimonial-control-btn" 
            onClick={handlePrev}
            aria-label="Anterior"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          
          <button 
            type="button" 
            className="testimonial-control-btn" 
            onClick={handlePause}
            aria-label="Pausar"
          >
            <i className="fas fa-pause"></i>
          </button>
          
          <button 
            type="button" 
            className="testimonial-control-btn" 
            onClick={handlePlay}
            aria-label="Reproducir"
          >
            <i className="fas fa-play"></i>
          </button>
          
          <button 
            type="button" 
            className="testimonial-control-btn" 
            onClick={handleNext}
            aria-label="Siguiente"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className="swiper testimonial-slider">
          <div className="swiper-wrapper">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="swiper-slide">
                <div className={`testimonial-items ${testimonial.backgroundStyle ? `style-${testimonial.backgroundStyle.replace('bg-', '')}` : ''}`}>
                  <div className="icon">
                    <img 
                      src={testimonial.quoteIcon || "/assets/kidsa/assets/img/quote.png"} 
                      alt="img" 
                    />
                  </div>
                  <div className={`testimonial-bg ${testimonial.backgroundStyle || ''}`}></div>
                  <div className="testimonial-content">
                    <p>{testimonial.quote}</p>
                    <h6>{testimonial.author}</h6>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="swiper-dot text-center pt-5">
            <div className="dot"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KidsaTestimonial;